import * as React from "react";
import {
  Bell,
  Settings,
  Search,
  Menu,
  X,
  ChevronDown,
  LogOut,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { userLogout } from "../../store/user/userSlice";
import ToggleTheme from "../ToggleTheme";
import { RootState } from "@/types";
import { useEffect, useRef } from "react";
import { useGetAllNotificationQuery } from "../../store/notificationApi";
import { useGetSingleStudentQuery } from "../../store/studentApi";
import { SidebarTrigger } from "../ui/sidebar";
// import { Input } from "../ui/input";
import useAutoLogout from "@/hooks/useAutoLogout";

const Navbar = () => {
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const { user } = useSelector((state: RootState) => state.user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { data: Student } = useGetSingleStudentQuery(user?._id);
  const { data: notifications } = useGetAllNotificationQuery();
  const filteredNotifications = React.useMemo(() => {
    if (!notifications || !Student) return [];
    return notifications?.find(
      (notification) => notification?.batch === Student?.batch,
    );
  }, [notifications, Student]);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const dispatch = useDispatch();

  const userName = `${
    user?.role === "student"
      ? user?.fname + " " + user?.lname
      : user?.name || user?.email
  }`;
  const userRole = `${user?.role == "user" ? "user" : user?.role}`;

  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useAutoLogout();

  return (
    <nav className="bg-background sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="w-12 h-12">
            <SidebarTrigger />
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <ToggleTheme />

            {/* Notifications */}
            <div className="relative">
              {userRole === "student" ? (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="group p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 relative"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <Bell className="h-5 w-5  text-foreground" />
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500" />
                </motion.button>
              ) : (
                ""
              )}

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className=" p-2 absolute sm:right-0 mt-2 sm:w-80 w-36 bg-white dark:bg-gray-800 rounded-lg border dark:text-white border-gray-100 shadow-lg py-2 z-50"
                  >
                    {filteredNotifications?.message ? (
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {filteredNotifications.message}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        No notifications for your batch yet.
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Settings */}
            <motion.button
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="group p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Settings className="h-5 w-5  text-primary" />
            </motion.button>

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-3 focus:outline-none"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="h-8 w-8 rounded-full border-foreground border overflow-hidden bg-[#26BDB0] flex items-center justify-center text-white font-semibold"
                >
                  {Student?.profile ? (
                    <img
                      className="object-cover object-center size-8"
                      src={Student?.profile}
                      alt="profile"
                    />
                  ) : (
                    userName.charAt(0)
                  )}
                </motion.div>
                <div className="hidden md:flex items-center space-x-1">
                  <span className="text-sm font-medium text-foreground dark:text-gray-300">
                    {userName}
                  </span>
                  <ChevronDown className="h-4 w-4 text-foreground" />
                </div>
              </button>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 flex">
                        <div className="h-8 w-8 rounded-full border-foreground border overflow-hidden bg-[#26BDB0] flex items-center justify-center text-white font-semibold mr-2">
                          {Student?.profile ? (
                            <img
                              className="object-cover object-center size-8"
                              src={Student?.profile}
                              alt="profile"
                            />
                          ) : (
                            userName.charAt(0)
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {userName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {userRole}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => dispatch(userLogout(user?.token))}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign out</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="hidden p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white dark:bg-gray-800 shadow-lg"
          >
            <div className="px-4 py-3 space-y-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg 
                           bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Search..."
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
